import { Box, Button, Grid, TextField, Autocomplete } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FlexBetween from "../../components/FlexBetween";
import { addEditConnectionRuntimeConfigSchema } from "../../schemas";
import useFetchConnectionRuntimeConfigById from "../../hooks/useFetchConnectionRuntimeConfigById";
import {
  addConnectionRuntimeConfig,
  updateConnectionRuntimeConfig,
} from "../../services/connectionRuntimeConfig";
import Loader from "../../components/Molecules/Loader/Loader";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ViewJsonSplitButton from "../../components/common/ViewJsonSplitButton";
import { IConnectionRuntimeConfig } from "../../types/ConnectionRuntimeConfig";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import { useToast } from "../../services/utils";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import { validateConfigRequest } from "../../services/resourcesService";
import { useAuth } from "../../contexts/AuthContext";

const CONNECTION_TYPE_OPTIONS = [
  "mariadb",
  "azure_sql",
  "oracle_sql",
  "snowflake_sql",
  "postgres_sql",
  "azure_sql_service_principal",
  "local",
  "blob",
  "sftp",
  "no_auth_api",
  "basic_auth_api",
  "token_auth_api",
  "key_auth_api",
  "oauth_api",
];

const AddEditConnectionRuntimeConfig: React.FC = () => {
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isAdmin } = useAuth();
  const { id: connectionRuntimeConfigId } = useParams();
  const [formData, setFormData] = useState<IConnectionRuntimeConfig>({
    connection_type: "",
    code: "",
    max_file_download_threads: 0,
    max_chunk_download_threads: 0,
    chunk_threshold_size_in_bytes: 0,
    max_num_of_file_chunk: 0,
    max_num_of_service_conn: 0,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [initialFormData, setInitialFormData] = useState<any>({});
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] =
    useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);

  // hooks
  const [connectionRuntimeConfigData] = useFetchConnectionRuntimeConfigById({
    setIsLoading,
    currentConnectionRuntimeConfigId: connectionRuntimeConfigId,
  });

  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: connectionRuntimeConfigData?.code,
      id: connectionRuntimeConfigData?.id,
    });
    return () => {
      setCurrentBreadcrumbPage({ name: "", id: null });
    };
  }, [connectionRuntimeConfigData]);

  const buildPayload = () => {
    // Mirrors the configFields object constructed in onSaveConnectionRuntimeConfig.
    // CREATE uses the flat payload; UPDATE wraps it in { connection_runtime_config: {...} }.
    const configFields = {
      connection_type: formData.connection_type,
      code: formData.code,
      max_file_download_threads:
        Number(formData.max_file_download_threads) || 0,
      max_chunk_download_threads:
        Number(formData.max_chunk_download_threads) || 0,
      chunk_threshold_size_in_bytes:
        Number(formData.chunk_threshold_size_in_bytes) || 0,
      max_num_of_file_chunk: Number(formData.max_num_of_file_chunk) || 0,
      max_num_of_service_conn: Number(formData.max_num_of_service_conn) || 0,
      is_active: formData.is_active !== undefined ? formData.is_active : true,
    };
    return connectionRuntimeConfigId
      ? { connection_runtime_config: configFields }
      : configFields;
  };

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const isEditMode = Boolean(connectionRuntimeConfigId);
      const validateReqBody = {
        target_endpoint: isEditMode
          ? `/connection-runtime-config/${connectionRuntimeConfigId}`
          : "/connection-runtime-config",
        method: isEditMode ? "PUT" : "POST",
        // PUT endpoint expects body wrapped in { connection_runtime_config: {...} }
        // POST endpoint accepts the flat payload directly
        body: isEditMode ? { connection_runtime_config: payload } : payload,
        query_params: {},
        url_params: isEditMode ? { id: connectionRuntimeConfigId } : {},
      };
      const response = await validateConfigRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) {
        showToast("Payload is valid!", "success");
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyPayload = async () => {
    const payload = buildPayload();
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    showToast("Payload copied to clipboard!", "success");
  };

  const handleFormData = (e: any) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    validateField(name, value);
  };

  const handleConnectionTypeChange = (value: string | null) => {
    setFormData({
      ...formData,
      connection_type: value || "",
    });
    validateField("connection_type", value || "");
  };

  const handleNumberFieldChange = (e: any) => {
    const { name, value } = e.target;
    const numValue = value === "" ? 0 : parseInt(value) || 0;
    setFormData({
      ...formData,
      [name]: numValue,
    });
  };

  const validateField = async (name: any, value: any) => {
    try {
      // Create a partial form data object with only the field being changed
      const partialFormData = { ...formData, [name]: value };
      await addEditConnectionRuntimeConfigSchema.validateAt(
        name,
        partialFormData
      );
      // If validation passes, clear any previous errors for this field
      setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
    } catch (validationError: any) {
      // If validation fails, set the error message for this field
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };

  // Handle save
  const onSaveConnectionRuntimeConfig = async (e: any) => {
    const serviceAction = connectionRuntimeConfigId
      ? updateConnectionRuntimeConfig
      : addConnectionRuntimeConfig;
    e.preventDefault();
    try {
      await addEditConnectionRuntimeConfigSchema.validate(formData, {
        abortEarly: false,
      });

      // Explicitly construct payload with only editable fields,
      // ensuring numeric types (strip read-only fields like created_by_user, modified_by_user, etc.)
      const configFields = {
        connection_type: formData.connection_type,
        code: formData.code,
        max_file_download_threads:
          Number(formData.max_file_download_threads) || 0,
        max_chunk_download_threads:
          Number(formData.max_chunk_download_threads) || 0,
        chunk_threshold_size_in_bytes:
          Number(formData.chunk_threshold_size_in_bytes) || 0,
        max_num_of_file_chunk: Number(formData.max_num_of_file_chunk) || 0,
        max_num_of_service_conn: Number(formData.max_num_of_service_conn) || 0,
        is_active: formData.is_active !== undefined ? formData.is_active : true,
      };

      // CREATE uses flat payload, UPDATE wraps in { connection_runtime_config: {...} }
      const reqBody = connectionRuntimeConfigId
        ? { connection_runtime_config: configFields }
        : configFields;

      serviceAction({
        currentConnectionRuntimeConfigId: connectionRuntimeConfigId,
        payload: reqBody,
      })
        .then((response) => {
          if (response) {
            showToast(
              `Connection Runtime Config ${
                connectionRuntimeConfigId ? "updated" : "created"
              } successfully!`,
              "success"
            );
            navigate("/connection-runtime-config");
          }
        })
        .catch((error) => {
          console.error(
            `Cannot ${
              connectionRuntimeConfigId ? "update" : "create"
            } connection runtime config`
          );
        });
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      validationErrors.inner.forEach(
        (error: { path: string | number; message: string }) => {
          newErrors[error.path] = error.message;
        }
      );
      setErrors((prevErrors) => ({
        ...prevErrors,
        ...newErrors,
      }));
    }
  };

  useEffect(() => {
    if (connectionRuntimeConfigData?.id) {
      setFormData({
        ...connectionRuntimeConfigData,
      });
      setInitialFormData(
        JSON.parse(JSON.stringify(connectionRuntimeConfigData))
      );
    }
  }, [connectionRuntimeConfigData]);

  // Update hasChanges when formData changes
  useEffect(() => {
    const formChanged =
      JSON.stringify(formData) !== JSON.stringify(initialFormData);

    setHasChanges(formChanged);
  }, [formData, initialFormData]);

  return (
    <Box>
      <Loader isLoading={isLoading} />
      <form onSubmit={onSaveConnectionRuntimeConfig} autoComplete="off">
        <Box className="text-box-card compact-text-box-card">
          <Grid container rowSpacing={1.5} columnSpacing={2.5}>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                Connection Type<span className="required-asterisk">*</span>
              </label>
              <Autocomplete
                options={CONNECTION_TYPE_OPTIONS}
                value={formData?.connection_type || null}
                onChange={(event, value) => handleConnectionTypeChange(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    className={`form-control-autocomplete ${
                      errors?.connection_type ? "has-error" : ""
                    }`}
                    error={!!errors?.connection_type}
                    helperText={errors?.connection_type || ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                Code<span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="code"
                disabled={!!connectionRuntimeConfigId}
                className={`form-control ${errors?.code ? "has-error" : ""}`}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                error={!!errors?.code}
                helperText={errors?.code || ""}
                value={formData?.code}
                onChange={(e: any) => {
                  if (!connectionRuntimeConfigData?.code) {
                    handleFormData(e);
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">Max File Download Threads</label>
              <TextField
                type="number"
                name="max_file_download_threads"
                className="form-control"
                value={formData?.max_file_download_threads || 0}
                onChange={handleNumberFieldChange}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">Max Chunk Download Threads</label>
              <TextField
                type="number"
                name="max_chunk_download_threads"
                className="form-control"
                value={formData?.max_chunk_download_threads || 0}
                onChange={handleNumberFieldChange}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">Chunk Threshold Size (bytes)</label>
              <TextField
                type="number"
                name="chunk_threshold_size_in_bytes"
                className="form-control"
                value={formData?.chunk_threshold_size_in_bytes || 0}
                onChange={handleNumberFieldChange}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">Max Num of File Chunk</label>
              <TextField
                type="number"
                name="max_num_of_file_chunk"
                className="form-control"
                value={formData?.max_num_of_file_chunk || 0}
                onChange={handleNumberFieldChange}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                Max Num of Service Connections
              </label>
              <TextField
                type="number"
                name="max_num_of_service_conn"
                className="form-control"
                value={formData?.max_num_of_service_conn || 0}
                onChange={handleNumberFieldChange}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        </Box>
        <Box>
          <FlexBetween
            gap="3rem"
            sx={{ marginTop: 4, display: "flex", justifyContent: "flex-end" }}
          >
            <ViewJsonSplitButton
              onViewJson={() => {
                setValidationResponse(null);
                setIsPayloadDialogOpen(true);
              }}
              onCopyJson={handleCopyPayload}
            />
            <Button
              type="submit"
              className="btn-orange"
              color="secondary"
              variant="contained"
              disabled={!hasChanges}
            >
              <SaveOutlinedIcon /> &nbsp; Save
            </Button>
          </FlexBetween>
        </Box>
      </form>

      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        dialogTitle="Connection Runtime Config JSON"
        payload={buildPayload()}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </Box>
  );
};

export default AddEditConnectionRuntimeConfig;
